import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../components/primitives/Panel';
import { useIsNarrowScreen } from '../promote/work/useIsNarrowScreen';
import { foldServiceRows, linkedRowCount, PANEL_ROWS_SHOWN, PANEL_ROWS_SHOWN_NARROW } from './servicesPanelRows';
import { servicesPanelMessages as m } from './servicesPanelMessages';
import { useServicesPanelRows } from './useServicesPanelRows';
import { useSaveServiceLink } from './useSaveServiceLink';
import { useServicesPanelState } from './useServicesPanelState';
import { ServiceRowView } from './ServiceRowView';
import { ServiceLinkEditor } from './ServiceLinkEditor';
import { FindItSheet } from './findIt/FindItSheet';
import { ServicesPanelLead } from './ServicesPanelLead';
import {
  EXTRAS_HOST_SERVICES,
  SOUNDCLOUD_EXTRAS_SERVICE,
  SPOTIFY_EXTRAS_SERVICE,
  YOUTUBE_EXTRAS_SERVICE,
} from './servicesPanelServices';
import { useSpotifyCanvas } from './spotifyCanvas/useSpotifyCanvas';
import { SpotifyExtras } from './spotifyCanvas/SpotifyExtras';
import { YouTubeExtras } from './youtubeExtras/YouTubeExtras';
import { SoundCloudExtras } from './soundCloudExtras/SoundCloudExtras';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for ServicesPanel; each part uses its own slice of it */
import styles from './ServicesPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ServicesPanelProps {
  accountId: number;
  item: CatalogItem;
  /** The demo artist: the linked rows with Open only (design d2). */
  readOnly: boolean;
}

/**
 * "Where it plays" (SoundCloud connector design d; spec 2026-10-15 D10): every main service of a
 * song in a fixed order, linked or not. One component on the release page Overview tab (design d1;
 * it moved from the Kit tab on 2026-10-19) and on the
 * song page (d2). Each row saves by itself at once (`PATCH .../catalog/:uuid { serviceLinks }`).
 * Three rows hold an extras box (design service-extras a1): Spotify (the Canvas), YouTube (the
 * full-song videos) and SoundCloud (the song upload, open to every artist since 2026-10-18).
 */
const ServicesPanelComponent: React.FC<ServicesPanelProps> = ({ accountId, item, readOnly }) => {
  const intl = useIntl();
  const isNarrow = useIsNarrowScreen();
  const rows = useServicesPanelRows({ item, readOnly });
  const links = useSaveServiceLink({ accountId, item });
  const state = useServicesPanelState();
  const canvas = useSpotifyCanvas({ accountId, catalogItemUuid: item.uuid });
  const { shown, folded } = useMemo(
    () =>
      foldServiceRows(
        rows,
        isNarrow ? PANEL_ROWS_SHOWN_NARROW : PANEL_ROWS_SHOWN,
        state.expanded,
        EXTRAS_HOST_SERVICES
      ),
    [isNarrow, rows, state.expanded]
  );
  const linked = linkedRowCount(rows);
  const { save, clearError } = links;
  const { stopEditing } = state;
  const saveEdit = useCallback(
    (integrationType: string, url: string | null) => {
      save(integrationType, url)
        .then((saved) => saved && stopEditing())
        .catch(console.error);
    },
    [save, stopEditing]
  );
  const cancelEdit = useCallback(
    (integrationType: string) => {
      clearError(integrationType);
      stopEditing();
    },
    [clearError, stopEditing]
  );
  const findItService = state.findIt;
  const saveFound = useCallback(
    (url: string) => (findItService === null ? Promise.resolve(false) : save(findItService.integrationType, url)),
    [findItService, save]
  );

  return (
    <Panel data-testid="services-panel">
      <PanelHead
        icon={<FontAwesomeIcon icon={faLink} className="me-2" />}
        title={intl.formatMessage(m.title)}
        subtitle={isNarrow ? undefined : intl.formatMessage(m.subtitle)}
        right={
          <span className={styles.count}>
            {intl.formatMessage(isNarrow ? m.countShort : m.count, { linked, total: rows.length })}
          </span>
        }
      />
      {item.kind === CatalogItemKind.unreleased && (
        <ServicesPanelLead title={item.title} releaseDate={item.releaseDate} />
      )}
      <ul className={styles.list}>
        {shown.map((row) => {
          // The extras boxes sit inside their rows, under the link (design service-extras a1). They do
          // not count in "N of M linked", and a row with a box never folds.
          let extras: React.ReactNode | undefined;
          if (row.integrationType === SPOTIFY_EXTRAS_SERVICE && canvas.data !== null) {
            extras = (
              <SpotifyExtras
                accountId={accountId}
                item={item}
                data={canvas.data}
                readOnly={readOnly}
                refetch={canvas.refetch}
                hide={canvas.hide}
              />
            );
          } else if (row.integrationType === YOUTUBE_EXTRAS_SERVICE) {
            extras = <YouTubeExtras accountId={accountId} item={item} readOnly={readOnly} />;
          } else if (row.integrationType === SOUNDCLOUD_EXTRAS_SERVICE) {
            extras = <SoundCloudExtras accountId={accountId} item={item} readOnly={readOnly} />;
          }
          return state.editing === row.integrationType ? (
            <ServiceLinkEditor
              key={row.integrationType}
              service={row}
              url={item.serviceLinks?.[row.integrationType] ?? null}
              isSaving={links.savingType === row.integrationType}
              error={links.errors[row.integrationType]}
              onSave={(url) => saveEdit(row.integrationType, url)}
              onCancel={() => cancelEdit(row.integrationType)}
            />
          ) : (
            <ServiceRowView
              key={row.integrationType}
              row={row}
              isSaving={links.savingType === row.integrationType}
              isHighlighted={links.justLinked === row.integrationType}
              onAction={state.onAction}
              extras={extras}
            />
          );
        })}
      </ul>
      {(folded.length > 0 || state.expanded) && (
        <button type="button" className={styles.more} onClick={state.toggleExpanded} aria-expanded={state.expanded}>
          {state.expanded ? intl.formatMessage(m.fewer) : intl.formatMessage(m.more, { count: folded.length })}
          {!state.expanded && !isNarrow && (
            <span className={styles.moreNames}>{folded.map((row) => row.name).join(', ')}</span>
          )}
        </button>
      )}
      {findItService !== null && (
        <FindItSheet
          service={findItService}
          song={{ title: item.title, artistName: item.artistName }}
          onSave={saveFound}
          onPasteInstead={state.pasteInstead}
          onClose={state.closeFindIt}
        />
      )}
    </Panel>
  );
};

export const ServicesPanel = TypedMemo(ServicesPanelComponent);
